import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import {
  ChartStoreAction,
  StorageNativeStoreAction,
} from "src/components/shared-components/enum/enumStoreAction";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import {
  chartLoadErrorAction,
  chartLoadLastModifiedFromApiAction,
  chartLoadListFromApiAction,
  chartLoadSuccessAction,
  chartUnsetKeyStorage,
} from "../actions/chart.action";
import { listChart } from "../selectors/chart.selector";
import { ChartService } from "src/service/chart.service";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { currentUserData } from "../selectors/authentication.selectors";

/**
 * Handle generic side ngrx effect from chart action
 */
@Injectable()
export class ChartEffects {
  constructor(
    private _actions$: Actions,
    private _chartService: ChartService,
    private _store: Store
  ) { }

  /**
   * Effect who catch main event and unset specific server key storage
   */
  public chartUnsetKeyEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(StorageNativeStoreAction.STORAGE_UNSET_ALL_AUTH_KEY),
      map((action) => chartUnsetKeyStorage())
    )
  );
  /**
   * Effet attached to ngrx store init, then start init initialization
   */
  public chartStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ChartStoreAction.CHART_LOAD_INIT_START),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listChart)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        const chartObject: GenericStoreApiData = combinedObject[2];

        if (combinedObject?.data) {
          return chartLoadLastModifiedFromApiAction({
            chartObject: chartObject.data,
          });
        } else {
          return chartLoadListFromApiAction();
        }
      })
    )
  );

  /**
   * Load full list of charts effect
   */
  public chartListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ChartStoreAction.CHART_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._chartService.getCharts().pipe(
          map((chartObject: GenericApiData) =>
            chartLoadSuccessAction({ chartObject })
          ),
          catchError(() => of(chartLoadErrorAction({ chartObject: null })))
        );
      })
    )
  );

  /**
   * Load full list of chart by last modified effect
   */
  public chartListLastModifiedLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ChartStoreAction.CHART_LOAD_LAST_MODIFIED_FROM_API),
      switchMap((action: any) => {
        return this._chartService
          .getChartsByLastModified(action.chartObject)
          .pipe(
            map((chartObject: GenericApiData) =>
              chartLoadSuccessAction({ chartObject })
            ),
            catchError(() => of(chartLoadErrorAction({ chartObject: null })))
          );
      })
    )
  );
}
